import React, { useState, useEffect, useRef, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Box, VStack, Text } from "@chakra-ui/react";
import { CTK_Logo } from "../../assets";
import "./LoadingScreen.css";

const InfiniteScrambleText = ({ words }) => {
  const [displayText, setDisplayText] = useState(words[0]);
  const intervalRef = useRef(null);
  const letters = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
  const [currentWordIndex, setCurrentWordIndex] = useState(0);

  const scramble = useCallback(
    (targetWord) => {
      let iteration = 0;
      const maxIterations = targetWord.length;

      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }

      intervalRef.current = setInterval(() => {
        setDisplayText(
          targetWord
            .split("")
            .map((char, index) => {
              if (index < iteration) {
                return targetWord[index];
              }
              if (targetWord[index] === " ") return " ";
              return letters[Math.floor(Math.random() * 26)];
            })
            .join(""),
        );

        if (iteration >= maxIterations) {
          clearInterval(intervalRef.current);
          setTimeout(() => {
            setCurrentWordIndex((prev) => (prev + 1) % words.length);
          }, 1000);
        }

        iteration += 1 / 3;
      }, 30);
    },
    [words],
  );

  useEffect(() => {
    scramble(words[currentWordIndex]);
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [currentWordIndex, scramble, words]);

  return (
    <motion.span
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="loading-text"
      style={{
        textShadow: "0 0 20px rgba(103, 120, 203, 0.5)",
        letterSpacing: "2px",
      }}
    >
      {displayText}
    </motion.span>
  );
};

const LoadingScreen = ({ onLoadingComplete }) => {
  const [isVisible, setIsVisible] = useState(true);
  const [progress, setProgress] = useState(0);
  const safetyTimeoutRef = useRef(null);
  const isMountedRef = useRef(true);

  const loadingWords = [
    "LOADING..",
    "RENDERING...",
    "CREATING..",
    "BUILDING...",
    "COMPILING...",
    "INITIALIZING..",
    "FINDING_MATCH...",
    "QUEUING..",
  ];

  const handleComplete = useCallback(() => {
    if (!isMountedRef.current) return;
    
    setProgress(100);
    
    if (safetyTimeoutRef.current) {
      clearTimeout(safetyTimeoutRef.current);
    }

    setTimeout(() => {
      if (!isMountedRef.current) return;
      setIsVisible(false);
      
      setTimeout(() => {
        if (!isMountedRef.current) return;
        onLoadingComplete?.();
      }, 600);
    }, 500);
  }, [onLoadingComplete]);

  useEffect(() => {
    isMountedRef.current = true;

    const loadResources = async () => {
      try {
        safetyTimeoutRef.current = setTimeout(() => {
          if (isMountedRef.current) {
            handleComplete();
          }
        }, 5000);

        const images = Array.from(document.images);
        
        const pendingImages = images.filter(img => !img.complete);
        const loadedImages = images.length - pendingImages.length;

        if (images.length > 0) {
          const initialProgress = Math.round((loadedImages / images.length) * 90);
          setProgress(initialProgress);
        } else {
          simulateProgress();
          return;
        }

        const imagePromises = pendingImages.map(img => {
          return new Promise((resolve) => {
            const timeout = setTimeout(() => {
              resolve();
            }, 3000);

            const onLoad = () => {
              clearTimeout(timeout);
              const loaded = images.filter(img => img.complete).length;
              const newProgress = Math.round((loaded / images.length) * 90);
              if (isMountedRef.current) {
                setProgress(newProgress);
              }
              resolve();
            };

            const onError = () => {
              clearTimeout(timeout);
              const loaded = images.filter(img => img.complete).length;
              const newProgress = Math.round((loaded / images.length) * 90);
              if (isMountedRef.current) {
                setProgress(newProgress);
              }
              resolve();
            };

            img.addEventListener("load", onLoad, { once: true });
            img.addEventListener("error", onError, { once: true });
          });
        });

        await Promise.race([
          Promise.all(imagePromises),
          new Promise(resolve => setTimeout(resolve, 4000))
        ]);

        handleComplete();
      } catch (error) {
        console.error("Loading screen error:", error);
        handleComplete();
      }
    };

    const simulateProgress = () => {
      let simProgress = 0;
      const interval = setInterval(() => {
        if (!isMountedRef.current) {
          clearInterval(interval);
          return;
        }
        simProgress += Math.random() * 15;
        if (simProgress >= 90) {
          simProgress = 90;
          clearInterval(interval);
          setTimeout(() => {
            handleComplete();
          }, 500);
        }
        setProgress(Math.round(simProgress));
      }, 300);
    };

    loadResources();

    return () => {
      isMountedRef.current = false;
      if (safetyTimeoutRef.current) {
        clearTimeout(safetyTimeoutRef.current);
      }
    };
  }, [handleComplete]);

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          initial={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.8, ease: "easeInOut" }}
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            height: "100vh",
            zIndex: 9999,
            justifyContent: "center",
            background: "#0a0a0f",
          }}
        >
          <Box
            position="absolute"
            top="20%"
            left="30%"
            width="400px"
            height="400px"
            borderRadius="50%"
            bg="radial-gradient(400px circle at center, rgba(255, 255, 255, 0.02), transparent 40%)"
            pointerEvents="none"
          />

          <Box
            position="absolute"
            top="50%"
            left="50%"
            transform="translate(-50%, -50%)"
            width="600px"
            height="600px"
            borderRadius="50%"
            bg="radial-gradient(400px circle at center, rgba(255, 255, 255, 0.02), transparent 40%)"
            pointerEvents="none"
            className="glow-pulse"
          />

          <Box
            position="absolute"
            bottom="20%"
            right="30%"
            width="400px"
            height="400px"
            borderRadius="50%"
            bg="radial-gradient(400px circle at center, rgba(255, 255, 255, 0.02), transparent 40%)"
            pointerEvents="none"
          />

          <VStack spacing={5} position="relative" zIndex={1}>
            <motion.div
              initial={{ scale: 0, rotate: -180 }}
              animate={{ scale: 1, rotate: 0 }}
              transition={{
                type: "spring",
                stiffness: 200,
                damping: 20,
                duration: 0.8,
              }}
            >
              <Box
                width={{ base: "80px", md: "100px" }}
                height={{ base: "80px", md: "100px" }}
                borderRadius="50%"
                bg="rgba(103, 120, 203, 0.05)"
                backdropFilter="blur(10px)"
                border="1px solid #ffffff1a"
                display="flex"
                alignItems="center"
                justifyContent="center"
                p={4}
              >
                <motion.img
                  src={CTK_Logo}
                  alt="CTK Logo"
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "contain",
                  }}
                  animate={{ rotate: 360 }}
                  transition={{
                    duration: 20,
                    repeat: Infinity,
                    ease: "linear",
                  }}
                />
              </Box>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.5 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.2, duration: 0.5, type: "spring" }}
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                position: "relative",
                minWidth: "250px",
                minHeight: "50px",
              }}
            >
              <Box
                position="absolute"
                width="300px"
                height="300px"
                borderRadius="50%"
                bg="radial-gradient(400px circle at center, rgba(255, 255, 255, 0.02), transparent 40%)"
                pointerEvents="none"
                className="glow-pulse"
              />

              <Text textAlign="center">
                <InfiniteScrambleText words={loadingWords} />
              </Text>
            </motion.div>
          </VStack>

          <Box display="flex" gap={2} alignItems="center">
            {[0, 1, 2].map((i) => (
              <Box
                key={i}
                width="8px"
                height="8px"
                borderRadius="50%"
                bg="radial-gradient(circle, rgba(255, 255, 255, 0.02), transparent 40%)"
                style={{
                  boxShadow: "0 0 10px rgba(103, 120, 203, 0.3)",
                }}
                className="dot-pulse"
                sx={{
                  animation: `dotPulse 1.5s ease-in-out infinite`,
                  animationDelay: `${i * 0.3}s`,
                }}
              />
            ))}
          </Box>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5, duration: 0.5 }}
            style={{ width: "100%", maxWidth: "200px" }}
          >
            <Box
              width="100%"
              height="6px"
              bg="rgba(103, 120, 203, 0.1)"
              borderRadius="full"
              overflow="hidden"
            >
              <motion.div
                initial={{ width: 0 }}
                animate={{ width: `${progress}%` }}
                transition={{ duration: 0.3, ease: "easeInOut" }}
                style={{
                  height: "100%",
                  background: "#6778cb",
                  borderRadius: "full",
                  boxShadow: "0 0 10px rgba(103, 120, 203, 0.5)",
                }}
              />
            </Box>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default LoadingScreen;